/* eslint complexity: ["error", 14] */

import { useEffect, useState, useCallback, useContext, forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { css, styled } from 'styled-components';
import { FORM_SECTIONS } from './FormIDs';
import AddressList from '../../../components/AddressList';
import { Paragraph, Box } from '@cof/plastic-components';
import SelectedAddressDetails from '../../../components/SelectedAddressDetails';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { withText } from '../../../text/textStore';
import PropTypes from 'prop-types';
import { FormUtilsContext } from '../QuickCheck';

const AddressLookUpSpinner = styled(LoadingSpinner)`
    ${({ theme }: any) => css`
        margin: ${theme.space.md} auto;
    `}
`;
AddressLookUpSpinner.displayName = 'AddressLookUpSpinner';

const AddressLookup = forwardRef(
    (
        {
            // @ts-expect-error TS2339: Property 'selectedAddress' does not exist on type ''.
            selectedAddress,
            // @ts-expect-error TS2339: Property 'setSelectedAddress' does not exist on type ''.
            setSelectedAddress,
            // @ts-expect-error TS2339: Property 'currentOrPrevious' does not exist on type ''.
            currentOrPrevious,
            // @ts-expect-error TS2339: Property 'submitting' does not exist on type ''.
            submitting,
            // @ts-expect-error TS2339: Property 'responseObj' does not exist on type ''.
            responseObj,
            // @ts-expect-error TS2339: Property 'Text' does not exist on type ''.
            Text: text,
            // @ts-expect-error TS2339: Property 'setSelectedCurrentAddress' does not exist on type ''.
            setSelectedCurrentAddress
        },
        ref
    ) => {
        const { data } = responseObj;
        const [addressList, setAddressList] = useState(null);
        // @ts-expect-error TS2339: Property 'showManualForm' does not exist on type 'null'.
        const { showManualForm, setShowManualForm } = useContext(FormUtilsContext);
        const isManualForm = showManualForm[currentOrPrevious];
        const { getValues, setValue, trigger } = useFormContext();
        const addresses = data?.matchedAddresses;
        const addressIds =
            currentOrPrevious === 'current' ? FORM_SECTIONS.Address.Current : FORM_SECTIONS.Address.Previous;

        const resetYearsAtAddress = useCallback(() => {
            // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
            setValue(addressIds.YearsAtAddress, '');
            // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
        }, [setValue, addressIds.YearsAtAddress]);

        const getAddressValue = (address: any, key: any) => {
            return address[key] || '';
        };
        const setManualAddressFieldValues = useCallback(
            (address: any) => {
                // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
                setValue(addressIds.HouseNumber, getAddressValue(address, 'houseNumber'), {
                    shouldTouch: true
                });
                // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
                setValue(addressIds.HouseName, getAddressValue(address, 'houseName'), {
                    shouldTouch: true
                });
                // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
                setValue(addressIds.FlatNumber, getAddressValue(address, 'flatNumber'), {
                    shouldTouch: true
                });
                // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
                setValue(addressIds.StreetName, getAddressValue(address, 'streetName'), {
                    shouldTouch: true
                });
                // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
                setValue(addressIds.AddressLine2, '', {
                    shouldTouch: true
                });
                // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
                setValue(addressIds.Town, getAddressValue(address, 'postTown'), {
                    shouldTouch: true
                });
                // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
                setValue(addressIds.County, '', {
                    shouldTouch: true
                });
                // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
                setValue(addressIds.ActualPostcode, getAddressValue(address, 'postcode'), {
                    shouldTouch: true
                });
            },
            [setValue, addressIds]
        );

        const clearAddress = useCallback(() => {
            // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
            setValue(addressIds.HouseNumber, '');
            // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
            setValue(addressIds.HouseName, '');
            // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
            setValue(addressIds.FlatNumber, '');
            // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
            setValue(addressIds.StreetName, '');
            // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
            setValue(addressIds.AddressLine2, '');
            // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
            setValue(addressIds.Town, '');
            // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
            setValue(addressIds.County, '');
            // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
            setValue(addressIds.ActualPostcode, '');
        }, [setValue, addressIds]);

        const onAddressSelect = useCallback(
            (address: any) => {
                resetYearsAtAddress();
                setManualAddressFieldValues(address);
                setSelectedAddress(address);
                if (setSelectedCurrentAddress) {
                    setSelectedCurrentAddress(address);
                }
            },
            [resetYearsAtAddress, setManualAddressFieldValues, setSelectedAddress, setSelectedCurrentAddress]
        );

        useEffect(() => {
            if (addresses) {
                setAddressList(addresses);

                if (addresses.length === 1 && !selectedAddress) {
                    onAddressSelect(addresses[0]);
                }
            }
        }, [addresses]);

        // Clear address list, selected address, and form fields when lookup fails (has error) or returns no results
        useEffect(() => {
            if (responseObj?.error || responseObj?.data?.matchedAddresses?.length === 0) {
                setAddressList(null);
                setSelectedAddress(null);
                clearAddress();
            }
        }, [responseObj?.error, responseObj?.data?.matchedAddresses, setSelectedAddress, clearAddress]);

        useEffect(() => {
            if (selectedAddress) {
                // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
                const requiredsToCheck = [addressIds.Town, addressIds.ActualPostcode];
                const hasMissingRequired = requiredsToCheck.some((field) => !getValues(field));
                if (hasMissingRequired) {
                    setShowManualForm((prev: any) => ({
                        ...prev,
                        [currentOrPrevious]: true
                    }));
                }
            }
        }, [selectedAddress]);

        useEffect(() => {
            if (selectedAddress && isManualForm) {
                // @ts-expect-error TS18048: 'addressIds' is possibly 'undefined'.
                [addressIds.Town, addressIds.ActualPostcode].forEach((field) => {
                    if (!getValues(field)) {
                        trigger(field);
                    }
                });
            }
        }, [selectedAddress, isManualForm]);

        const [hasHadSelectedAddress, setHasHadSelectedAddress] = useState(false);

        useEffect(() => {
            if (selectedAddress) {
                setHasHadSelectedAddress(true);
            }
        }, [selectedAddress]);

        useEffect(() => {
            if (selectedAddress === null && hasHadSelectedAddress) {
                clearAddress();
            }
        }, [selectedAddress, hasHadSelectedAddress, clearAddress]);

        const showAddressSpinner = submitting;
        // @ts-expect-error TS2339: Property 'length' does not exist on type 'never'.
        const showAddressListComponent = !submitting && addressList && addressList.length > 1 && !selectedAddress;
        return (
            // @ts-expect-error TS2769: No overload matches this call.
            <Box ref={ref} data-qa-id={addressIds.AddressLookup} id={addressIds.AddressLookup}>
                {showAddressListComponent && (
                    <AddressList
                        id={`address:${currentOrPrevious}-address:list`}
                        label={text({
                            path:
                                currentOrPrevious === 'current'
                                    ? 'CurrentAddress.ListTitle'
                                    : 'PreviousAddress.ListTitle'
                        })}
                        options={addressList}
                        onAddressSelect={onAddressSelect}
                    />
                )}

                {showAddressSpinner && (
                    <AddressLookUpSpinner
                        circleSize={[30, null, 50]}
                        circleThickness={['2px', null, '3px']}
                        Icon={null}
                        data-qa-id="address-lookup-spinner"
                    >
                        <Paragraph mt="sm">{text({ path: 'LoadingMessage' })} </Paragraph>
                    </AddressLookUpSpinner>
                )}

                {!isManualForm && selectedAddress && (
                    <SelectedAddressDetails
                        address={selectedAddress.formattedAddress}
                        selectDifferentAddress={() => {
                            clearAddress();
                            setSelectedAddress(null);
                        }}
                        addressType={`${currentOrPrevious}-address`}
                    />
                )}
            </Box>
        );
    }
);

AddressLookup.propTypes = {
    selectedAddress: PropTypes.shape({
        formattedAddress: PropTypes.string
    }),
    setSelectedAddress: PropTypes.func.isRequired,
    currentOrPrevious: PropTypes.oneOf(['current', 'previous']).isRequired,
    findAddress: PropTypes.func,
    submitting: PropTypes.bool.isRequired,
    responseObj: PropTypes.shape({
        error: PropTypes.any,
        data: PropTypes.shape({
            matchedAddresses: PropTypes.array
        }),
        response: PropTypes.any,
        execCount: PropTypes.number
    }).isRequired,
    Text: PropTypes.func.isRequired,
    setSelectedCurrentAddress: PropTypes.func
};

export default withText('Address')(AddressLookup);
